import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPartyData } from '../lib/api';
import { PartyResponse, SkillName, Ability } from '@shared/types';
import { SKILLS, ABILITY_NAMES, ABILITY_ORDER, SKILLS_ORDERED, ABILITY_PASTELS, ABILITY_PASTELS_LIGHT } from '@shared/dnd-data';
import PartyRadar from '../components/PartyRadar';
import WordCloud from '../components/WordCloud';
import SkillReferencePanel from '../components/SkillReferencePanel';
import { useTheme } from '../lib/useTheme';

type RadarTab = 'skills' | 'savingThrows' | 'languages' | 'tools';

export default function PartyViewPage() {
  const { slug } = useParams<{ slug: string }>();
  const { theme } = useTheme();
  const [data, setData] = useState<PartyResponse | null>(null);
  const [tab, setTab] = useState<RadarTab>('skills');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getPartyData(slug)
      .then((partyData) => {
        setData(partyData);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error) return <div className="text-center py-8 text-dnd-danger-text">{error}</div>;
  if (!data) return <div className="text-center py-8">No data</div>;

  const includedCharacters = data.characters.filter((ch) => !ch.excluded);

  // WHY: SKILLS_ORDERED groups skills by ability (STR→CHA) then alphabetizes,
  // so the radar axis order is deterministic and not tied to object insertion.
  const skillLabels = SKILLS_ORDERED.map((key) => SKILLS[key].displayName);
  const skillValues = SKILLS_ORDERED.map((key) => data.radar.skills[key] || 0);
  const pastels = theme === 'light' ? ABILITY_PASTELS_LIGHT : ABILITY_PASTELS;
  const skillPointColors = SKILLS_ORDERED.map((key) => pastels[SKILLS[key].ability]);

  const savingLabels = ABILITY_ORDER.map((key) => ABILITY_NAMES[key]);
  const savingValues = ABILITY_ORDER.map((key) => data.radar.savingThrows[key] || 0);
  const savingPointColors = ABILITY_ORDER.map((key) => pastels[key]);

  const labels = tab === 'skills' ? skillLabels : savingLabels;
  const values = tab === 'skills' ? skillValues : savingValues;
  const pointColors = tab === 'skills' ? skillPointColors : savingPointColors;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="heading text-2xl font-bold">{data.campaign.name}</h2>
          <p className="text-dnd-muted">Party View &bull; Level {data.campaign.level}</p>
        </div>
        <Link to={`/${slug}`} className="btn btn-secondary">
          Back to Campaign
        </Link>
      </div>

      <SkillReferencePanel characterSkills={data.characterSkills || []} />

      <div className="card mb-6">
        <div className="flex flex-nowrap gap-2 mb-2 radar-tab-buttons overflow-x-auto">
          <button
            onClick={() => setTab('skills')}
            aria-pressed={tab === 'skills'}
            className={`btn ${tab === 'skills' ? 'btn-gold' : 'tab-btn'}`}
          >
            Skills
          </button>
          <button
            onClick={() => setTab('savingThrows')}
            aria-pressed={tab === 'savingThrows'}
            className={`btn ${tab === 'savingThrows' ? 'btn-gold' : 'tab-btn'}`}
          >
            Saving Throws
          </button>
          <button
            onClick={() => setTab('languages')}
            aria-pressed={tab === 'languages'}
            className={`btn ${tab === 'languages' ? 'btn-gold' : 'tab-btn'}`}
          >
            Languages
          </button>
          <button
            onClick={() => setTab('tools')}
            aria-pressed={tab === 'tools'}
            className={`btn ${tab === 'tools' ? 'btn-gold' : 'tab-btn'}`}
          >
            Tools
          </button>
        </div>

        {tab === 'languages' ? (
          <WordCloud frequencies={data.languageFrequencies || {}} />
        ) : tab === 'tools' ? (
          <WordCloud frequencies={data.toolFrequencies || {}} />
        ) : (
          <div>
            <PartyRadar labels={labels} values={values} pointColors={pointColors} />
          </div>
        )}
      </div>

    </div>
  );
}
